import { ScheduleItemDto } from "../features/calendar/calendar.types";

export const getDurationFromScheduleItem = (item: ScheduleItemDto) => {
  return getDurationBetweenDates(
    new Date(item.scheduleStart),
    new Date(item.scheduleEnd),
  );
};

/**
 * @returns duration, durationInHours, durationInMinutes
 * */
export const getDurationBetweenDates = (
  startDate: Date | string,
  endDate: Date | string,
) => {
  const a = new Date(startDate);
  const b = new Date(endDate);
  const duration = b.getTime() - a.getTime();
  const durationInMinutes = duration / 1000 / 60;
  const durationInHours = Number((durationInMinutes / 60).toPrecision(2));

  return {
    duration,
    durationInHours,
    durationInMinutes,
  };
};

export const checkTimeFormat = (inputValue: string) => {
  if (/^\d*:[0-5]?[0-9]?$/.test(inputValue)) {
    return inputValue;
  }
  return false;
};

export const millisToStopwatch = (millis: number) => {
  return formatTimeStopwatchSeconds(millis % 1000);
};

export const formatTimeStopwatchMinutes = (
  secondsInput: number | undefined,
) => {
  if (secondsInput === undefined) return "0:00";
  const hours = Math.floor(secondsInput / 3600);
  const minutes = Math.floor((secondsInput % 3600) / 60);
  return `${`0${hours}`.slice(-2)}:${`0${minutes}`.slice(-2)}`;
};

export const formatTimeStopwatchSeconds = (
  secondsInput: number | undefined,
) => {
  if (secondsInput === undefined) return "0:00:00";
  const hours = Math.floor(secondsInput / 3600);
  const minutes = Math.floor((secondsInput % 3600) / 60);
  const seconds = Math.floor(secondsInput % 60);
  return `${`0${hours}`.slice(-2)}:${`0${minutes}`.slice(
    -2,
  )}:${`0${seconds}`.slice(-2)}`;
};

export const parseTimeMinutes = (time: string) => {
  const [hours, minutes] = time.split(":").map((val) => parseInt(val) || 0);
  const durationInSeconds = (minutes + 60 * hours) * 60;
  return { hours, minutes, durationInSeconds };
};

export const parseTime = (time: string) => {
  const [hours, minutes, seconds] = time
    .split(":")
    .map((val) => parseInt(val) || 0);
  const durationInSeconds = seconds + 60 * (minutes + 60 * hours);
  return { hours, minutes, seconds, durationInSeconds };
};

export const durationInSeconds = (time: string) => {
  const [hours, minutes, seconds] = time
    .split(":")
    .map((val) => parseInt(val) || 0);
  return seconds + 60 * (minutes + 60 * hours);
};

export const getNow = () => {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const numeric = hours + minutes / 60;
  return { hours, minutes, numeric };
};

export const getTimeFromString = (dateString: string) => {
  return new Date(dateString).getTime();
};

// helper: get [localStartMidnight, localNextMidnight] as ISO strings (UTC-encoded)
export function toLocalDayRangeISO(d: Date | string | number) {
  const dt = new Date(d);
  const start = new Date(dt); // copy
  start.setHours(0, 0, 0, 0); // local midnight
  const end = new Date(start);
  end.setDate(end.getDate() + 1); // next local midnight
  return [start.toISOString(), end.toISOString()];
}

export const getEndOfDayLocal = (date: Date | string | number) => {
  const endDateLate = new Date(date); // copy so original isn't changed
  endDateLate.setDate(endDateLate.getDate() + 1);
  endDateLate.setHours(0, 0, 0, 0); // midnight local time next day
  return endDateLate;
};

/**
 * Last moment (23:59:59.999, local) of the calendar day a date input
 * returns ("YYYY-MM-DD") - for saving deadlines. Unlike getEndOfDayLocal,
 * which is the exclusive end (next day's midnight) and showed the deadline
 * one day late; the string is also read as a local date, not UTC midnight.
 */
export const getEndOfLocalDay = (value: string) => {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999);
};

export const getStartOfDayLocal = (date: Date | string | number) => {
  const endDateEarly = new Date(date); // copy so original isn't changed
  endDateEarly.setHours(0, 0, 0, 0); // midnight local time next day
  return endDateEarly;
};

export const getEndOfDayUTC = (date: Date | string | number) => {
  const d = getStartOfDayUTC(date); // normalize first
  d.setUTCDate(d.getUTCDate() + 1); // next midnight UTC (exclusive end)
  return d;
};

export const getStartOfDayUTC = (dateInput: Date | string | number): Date => {
  const d = new Date(dateInput);

  if (isNaN(d.getTime())) {
    throw new Error(`Invalid date argument provided: ${dateInput}`);
  }

  // Construct pure UTC date using the Year, Month, and Date parts
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  );
};

/**
 * Converts a Date that represents a calendar day in local time (e.g. a
 * `days`/`weekdays` array entry built via `new Date(year, month, date)`, or
 * `new Date()` for "today") into UTC midnight of that same calendar day.
 *
 * Use this instead of `getStartOfDayUTC` whenever the input's date is meant
 * to be read from its *local* Y/M/D. `getStartOfDayUTC` reads the *UTC*
 * Y/M/D instead, which is correct for values that already encode their date
 * via UTC (e.g. `scheduleStart` from the backend) but silently shifts a
 * local-midnight Date to the wrong day whenever the viewer's timezone offset
 * puts local midnight on a different UTC calendar date.
 */
export const getUTCMidnightForLocalDay = (
  dateInput: Date | string | number,
): Date => {
  const d = new Date(dateInput);

  if (isNaN(d.getTime())) {
    throw new Error(`Invalid date argument provided: ${dateInput}`);
  }

  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
};
